import React, { useCallback, useMemo, useState } from 'react';

import EcModal from '~/components/Shared/EcModal/EcModal';
import { Button } from '~/eds/src/components/Button/Button';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Table } from '~/eds/src/modules/Table/Table';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { MODAL_NAME } from '~/types/modal.types';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';

const SelectGroup = ({ onSelectGroup, onNewGroupCreated }) => {
  const intl = useIntl();
  const [filter, setFilter] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);

  const { data: documentGroupsData, isFetching: isFetchingDocumentGroups } =
    api.endpoints.getAllDocumentGroups.useQuery();

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage({
          id: 'SelectGroup.nameColumnTitle',
          defaultMessage: 'Name',
          description: 'Column title for group name',
        }),
        renderCell: (group) => {
          return (
            <Link onClick={() => onSelectGroup?.(group.groupId)}>
              {group.name?.trim()}
            </Link>
          );
        },
      },
      {
        key: 'createdBy',
        title: intl.formatMessage({
          id: 'SelectGroup.createdByColumnTitle',
          defaultMessage: 'Created by',
          description: 'Column title for group creator',
        }),
        renderCell: (group) => {
          return <Text variant="body1">{group.createdBy}</Text>;
        },
      },
      {
        key: 'dateAdded',
        title: intl.formatMessage({
          id: 'SelectGroup.createdOnDateColumnTitle',
          defaultMessage: 'Created on date',
          description: 'Column title for group creation date',
        }),
        cellType: 'datetime',
        mapCellProps: (group) => ({
          datetime: group.dateAdded,
        }),
      },
      {
        key: 'lastModified',
        title: intl.formatMessage({
          id: 'SelectGroup.lastUpdatedColumnTitle',
          defaultMessage: 'Last updated',
          description: 'Column title for group last modified date',
        }),
        cellType: 'datetime',
        mapCellProps: (group) => ({
          datetime: group.lastModified,
        }),
      },
      {
        key: 'numberOfDocs',
        title: intl.formatMessage({
          id: 'SelectGroup.numberOfDocumentsColumnTitle',
          defaultMessage: 'Number of documents',
          description: 'Column title for document count in group',
        }),
        mapCellProps: (group) => ({
          text: group.numberOfDocs,
        }),
      },
    ],
    [],
  );
  const handleSearch = useCallback((value) => setFilter(value), []);

  return (
    <Box mr={4} ml={4}>
      <FlexLayout mb={8}>
        <Text variant="subtitle">
          <FormattedMessage
            id="SelectGroup.selectGroupInstructions"
            defaultMessage="Select which group you want add this document to"
            description="Instructions for selecting a group"
          />
        </Text>
      </FlexLayout>
      <FlexLayout mb={8}>
        <Box sx={{ flex: 1 }}>
          <SearchInput
            name="groups"
            placeholder={intl.formatMessage({
              id: 'SelectGroup.searchGroupsPlaceholder',
              defaultMessage: 'Search groups',
              description: 'Placeholder text for group search input',
            })}
            value={filter}
            onChange={handleSearch}
          />
        </Box>

        <FlexLayout ml={8}>
          <Button
            text={intl.formatMessage({
              id: 'SelectGroup.newGroupButton',
              defaultMessage: '+ New Group',
              description: 'Button text for creating a new group',
            })}
            onClick={() => setShowNewGroup(true)}
          />
        </FlexLayout>
      </FlexLayout>

      <LoadingContainer isLoading={isFetchingDocumentGroups}>
        {documentGroupsData && (
          <Table
            name={intl.formatMessage({
              id: 'SelectGroup.groupsResultTableName',
              defaultMessage: 'groups result',
              description: 'Table name for groups search results',
            })}
            totalCount={documentGroupsData.count}
            columns={columns}
            data={documentGroupsData.results
              .map((group) => {
                return { ...group, id: group.groupId };
              })
              .filter(
                (group) =>
                  group.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0,
              )}
            options={{
              enableExportXlsx: false,
              enableSelectRows: false,
              enableManageColumns: false,
            }}
            reactTableOptions={{
              autoResetSortBy: false,
              disableSortRemove: true,
            }}
          />
        )}
      </LoadingContainer>
      {showNewGroup && (
        <EcModal
          modalType={MODAL_NAME}
          width="560px"
          title={intl.formatMessage({
            id: 'SelectGroup.newGroupModalTitle',
            defaultMessage: 'New Group',
            description: 'Modal title for creating a new group',
          })}
          labelText={intl.formatMessage({
            id: 'SelectGroup.groupNameLabel',
            defaultMessage: 'Group Name',
            description: 'Label for group name input field',
          })}
          includeTextArea={true}
          labelTextArea={intl.formatMessage({
            id: 'SelectGroup.notesLabel',
            defaultMessage: 'Notes',
            description: 'Label for notes text area',
          })}
          confirmButtonText={intl.formatMessage({
            id: 'SelectGroup.createGroupButton',
            defaultMessage: 'Create Group',
            description: 'Button text for creating a group',
          })}
          handleNameChange={(name, note) => onNewGroupCreated?.({ name, note })}
          hideModal={() => setShowNewGroup(false)}
        />
      )}
    </Box>
  );
};

export default SelectGroup;
